import { useEffect, useState } from 'react';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Empty, Stat } from '../components/ui.jsx';
import { ConfirmModal } from '../components/Modal.jsx';
import { IconRefresh } from '../components/Icons.jsx';
import { removeFromOutbox, subscribeOutbox } from '../lib/outbox.js';
import { useConnection } from '../lib/hooks.js';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { formatDateTime, num } from '../lib/format.js';

/**
 * What is still waiting to reach the server.
 *
 * Anything the server rejected on its merits stays here with the reason
 * attached rather than disappearing, so a sale is never lost quietly.
 */
export default function Queue() {
  const [items, setItems] = useState([]);
  const [discarding, setDiscarding] = useState(null);
  const { online, syncing, sync } = useConnection();
  const toast = useToast();

  useEffect(() => subscribeOutbox(setItems), []);

  const runSync = async () => {
    const result = await sync();
    if (result.sent) toast.success(t.connection.sent(result.sent));
    else if (result.failed) toast.error(t.queue.rejectedToast);
    else if (result.remaining) toast.warn(t.connection.stillWaiting);
    else toast.info(t.connection.nothingWaiting);
  };

  const rejected = items.filter((i) => i.error);

  return (
    <>
      <PageHeader
        title={t.queue.title}
        subtitle={t.queue.subtitle}
        back="/"
        actions={
          <button type="button" className="btn btn-sm btn-primary" onClick={runSync} disabled={syncing || !online}>
            {syncing ? <span className="spinner" /> : <IconRefresh />} {t.connection.sendNow}
          </button>
        }
      />

      <div className="page">
        <div className="stat-grid">
          <Stat label={t.queue.waiting} value={num(items.length)} />
          <Stat label={t.queue.rejected} value={num(rejected.length)} tone={rejected.length ? 'danger' : ''} />
          <Stat
            label={t.queue.connection}
            value={online ? t.connection.online : t.queue.title}
            tone={online ? 'success' : 'danger'}
          />
        </div>

        {rejected.length > 0 && <Alert tone="error">{t.queue.rejectedNotice(num(rejected.length))}</Alert>}

        {items.length === 0 ? (
          <Card>
            <Empty title={t.queue.nothingWaiting} message={t.queue.nothingWaitingHint} />
          </Card>
        ) : (
          <div className="record-list">
            {items.map((item) => (
              <div key={item.id} className="record">
                <div className="record-top">
                  <span className="title">{item.label || item.path}</span>
                  <span className={`badge ${item.error ? 'danger' : 'debt'}`}>
                    {item.error ? t.queue.rejected : t.queue.waiting}
                  </span>
                </div>
                <div className="record-meta">
                  <span>{formatDateTime(item.queued_at)}</span>
                  <span>{t.queue.attempts(num(item.attempts))}</span>
                </div>
                {item.error && <Alert tone="error">{item.error}</Alert>}
                <div className="btn-row" style={{ marginTop: 4 }}>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setDiscarding(item)}>
                    {t.queue.discard}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {discarding && (
        <ConfirmModal
          title={t.queue.discardTitle}
          message={t.queue.discardMessage(discarding.label || discarding.path)}
          confirmLabel={t.queue.discard}
          onClose={() => setDiscarding(null)}
          onConfirm={async () => {
            await removeFromOutbox(discarding.id);
            setDiscarding(null);
            toast.info(t.queue.discarded);
          }}
        />
      )}
    </>
  );
}
